/**
 * Evidences service
 */

import { apiClient } from '../utils/api';
import type { Evidence, ApiResponse } from '../../../shared/src/types';

export interface GenerateUploadUrlData {
  installation_id: string;
  checklist_run_id: string;
  checklist_item_run_id?: string;
  filename: string;
  content_type: string;
}

export interface GenerateUploadUrlResponse {
  uploadUrl: string;
  evidenceId: string;
  r2Key: string;
}

export async function generateUploadUrl(
  data: GenerateUploadUrlData
): Promise<GenerateUploadUrlResponse> {
  const response = await apiClient.post<ApiResponse<GenerateUploadUrlResponse>>(
    '/evidences/upload-url',
    data
  );

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error generating upload URL');
  }

  if (!response.data.data) {
    throw new Error('Invalid response from server');
  }

  return response.data.data;
}

export async function uploadEvidence(
  evidenceId: string,
  file: File
): Promise<Evidence> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.put<ApiResponse<Evidence>>(
    `/evidences/${evidenceId}/upload`,
    file,
    {
      headers: {
        'Content-Type': file.type,
      },
    }
  );

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error uploading evidence');
  }

  if (!response.data.data) {
    throw new Error('Invalid response from server');
  }

  return response.data.data;
}

export async function listEvidences(checklistRunId?: string, checklistItemRunId?: string): Promise<Evidence[]> {
  const queryParams = new URLSearchParams();
  if (checklistRunId) queryParams.append('checklist_run_id', checklistRunId);
  if (checklistItemRunId) queryParams.append('checklist_item_run_id', checklistItemRunId);

  const url = `/evidences${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await apiClient.get<ApiResponse<Evidence[]>>(url);

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error fetching evidences');
  }

  return response.data.data || [];
}

export async function getEvidence(id: string): Promise<Evidence> {
  const response = await apiClient.get<ApiResponse<Evidence>>(`/evidences/${id}`);

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error fetching evidence');
  }

  if (!response.data.data) {
    throw new Error('Evidence not found');
  }

  return response.data.data;
}

export async function downloadEvidence(id: string): Promise<Blob> {
  const response = await apiClient.get(`/evidences/${id}/download`, {
    responseType: 'blob',
  });

  return response.data;
}

export async function deleteEvidence(id: string): Promise<void> {
  const response = await apiClient.delete<ApiResponse<{ success: boolean }>>(`/evidences/${id}`);

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error deleting evidence');
  }
}

